import React, { useState, useMemo } from "react";
import toast from "react-hot-toast";
import { useAuthStore } from "@/store/authStore";
import { CustomersControlPanel } from "../components/CustomersControlPanel";
import { CustomersKanban } from "../components/CustomersKanban";
import { CustomersStats } from "../components/CustomersStats";
import { ActivityStats } from "../components/ActivityStats";
import { CustomersTableView } from "../components/CustomersTableView";
import { UploadsHistoryView } from "../components/UploadsHistoryView";
import { UploadsGridView } from "../components/UploadsGridView";
import { AssignCustomersView } from "../components/AssignCustomersView";
import { Pagination, ConfirmationModal, TableActionButton } from "@/components/ui";
import { UploadCloud } from "lucide-react";
import { CONTACT_STAGES, getVisibleContactStagesForRole } from "../constants/contactConstants";
import { AddDesign } from "../../client_contact_stages/verve-design/components/AddDesign";
import { DesignerUpload } from "../../client_contact_stages/verve-design/components/designer_upload";
import { useContactPermissions } from "../hooks/useContactPermissions";
import { ContactModals } from "../components/ContactModals";
import { RequestRevertModal } from "../components/RequestRevertModal";
import { normalizeCustomer } from "../utils/normalization";
import { useVerveContacts, useActivityStats } from "../hooks/useVerveContacts";
import { useCustomerFilters } from "../hooks/useCustomerFilters";
import { useTelecallers } from "../hooks/useTelecallers";
import { useContactActivities } from "../hooks/useContactActivities";
import { useClientPagination } from "@/hooks";
import { useUsers } from "@/hooks";

export default function ContactScreen() {
  const { user } = useAuthStore();
  const { isAdmin } = useContactPermissions(user);

  const visibleStages = useMemo(() => {
    return getVisibleContactStagesForRole(CONTACT_STAGES, user);
  }, [user]);

  // 1. UI State & Filters
  const {
    search, setSearch, debouncedSearch,
    view, setView,
    viewStyle, setViewStyle,
    page, setPage,
    sortBy, setSortBy,
    filters, setFilters,
    showAdvancedFilters, setShowAdvancedFilters
  } = useCustomerFilters();

  const [isStatsExpanded, setIsStatsExpanded] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [revertRequest, setRevertRequest] = useState(null);
  const [designUploadLead, setDesignUploadLead] = useState(null);

  // 2. Data Fetching
  // 2. Query Params
  const tableParams = useMemo(() => ({
    search: debouncedSearch,
    status: filters.status !== 'all' ? filters.status : undefined,
    activityStatus: filters.activityStatus !== 'all' ? filters.activityStatus : undefined,
    assignedTo: filters.assignee !== 'all' ? filters.assignee : undefined,
    date: filters.date !== 'all' ? filters.date : undefined,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    sortBy
  }), [debouncedSearch, filters.status, filters.activityStatus, filters.assignee, filters.date, filters.fromDate, filters.toDate, sortBy]);

  const statsParams = useMemo(() => ({
    search: debouncedSearch,
    status: filters.status !== 'all' ? filters.status : undefined,
    activityStatus: filters.activityStatus !== 'all' ? filters.activityStatus : undefined,
    assignedTo: filters.assignee !== 'all' ? filters.assignee : undefined,
    date: filters.date !== 'all' ? filters.date : undefined,
    fromDate: filters.fromDate,
    toDate: filters.toDate
  }), [debouncedSearch, filters.status, filters.activityStatus, filters.assignee, filters.date, filters.fromDate, filters.toDate]);

  const {
    contacts, total, isLoading, refetch,
    importHistory, isHistoryLoading,
    stats: globalStats, isStatsLoading,
    deleteContact, bulkImport, updateContact, deleteImport
  } = useVerveContacts(tableParams, statsParams);

  const { data: activityStats } = useActivityStats(statsParams);

  const { telecallers, loading: telecallersLoading } = useTelecallers();
  const { teamMembers: teamData } = useUsers({ limit: 1000, includeOwner: true });

  // 3. Modal State
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editData, setEditData] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [actNote, setActNote] = useState("");
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", message: "", onConfirm: null, loading: false });

  const activeContact = useMemo(() => {
    if (!detail) return null;
    return contacts.find(c => c._id === detail._id) || detail;
  }, [detail, contacts]);

  const updateStatus = async (customer, targetStatus) => {
    const currentStatus = customer.status === "drop" ? "drop" : (customer.teleStatus || customer.status);
    const currentIndex = CONTACT_STAGES.findIndex(s => s.key === currentStatus);
    const targetIndex = CONTACT_STAGES.findIndex(s => s.key === targetStatus);

    if (currentIndex !== -1 && targetIndex !== -1 && targetIndex < currentIndex) {
      setRevertRequest({ contact: customer, targetStatus });
      return;
    }

    const pipelineSequence = [
      'appointment_scheduled',
      'site_visit',
      'initial_design',
      'initial_quotation',
      'client_approval',
      'converted'
    ];
    const currentPipeIdx = pipelineSequence.indexOf(currentStatus);
    const targetPipeIdx = pipelineSequence.indexOf(targetStatus);

    if (targetPipeIdx !== -1) {
      if (currentPipeIdx === -1 && targetPipeIdx > 0) {
        toast.error("Complete the before step (Appointment Scheduled) to move forward.");
        return;
      }
      if (currentPipeIdx !== -1 && targetPipeIdx > currentPipeIdx + 1) {
        const nextRequiredStageKey = pipelineSequence[currentPipeIdx + 1];
        const nextRequiredStageLabel = CONTACT_STAGES.find(s => s.key === nextRequiredStageKey)?.label || "before step";
        toast.error(`Complete the before step (${nextRequiredStageLabel}) to move forward.`);
        return;
      }
    }

    if (targetStatus === 'initial_quotation') {
      try {
        await updateContact({
          id: customer._id,
          data: { status: targetStatus, teleStatus: targetStatus, notes: "Status changed directly to initial_quotation" }
        });
      } catch (err) {
        toast.error("Failed to update status");
      }
      return;
    }
    setPendingStatus({ customer, targetStatus });
  };

  const confirmStatusUpdate = async (modalData) => {
    if (!pendingStatus) return;
    const { customer, targetStatus } = pendingStatus;
    const { note, ...extraData } = modalData;

    await updateContact({
      id: customer._id,
      data: { status: targetStatus, teleStatus: targetStatus, notes: note, ...extraData }
    });

    setPendingStatus(null);
  };

  const handleDirectRevert = async () => {
    if (!revertRequest) return;
    const { contact, targetStatus } = revertRequest;
    try {
      await updateContact({
        id: contact._id,
        data: {
          status: targetStatus,
          teleStatus: targetStatus,
          notes: `Stage reverted directly by Owner to ${targetStatus}`
        }
      });
      toast.success(`Stage reverted to ${targetStatus}`);
      setRevertRequest(null);
    } catch (err) {
      toast.error("Failed to revert status");
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const { downloadContactsAsExcel } = await import("../utils/templateUtils"); // Assuming this exists or I'll create it
      await downloadContactsAsExcel(leads);
      toast.success("Export started");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const toggleDisableCustomer = (id, currentStatus) => {
    const isEnabling = !!currentStatus;
    setConfirmState({
      isOpen: true,
      title: isEnabling ? "Enable Customer" : "Disable Customer",
      message: `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} this customer? ${isEnabling ? 'They will be restored to the active list.' : 'They will be hidden from the main view.'}`,

      variant: isEnabling ? "info" : "danger",
      onConfirm: async () => {
        setConfirmState(p => ({ ...p, loading: true }));
        try {
          const newStatus = !currentStatus;
          await updateContact({ id, data: { isDisabled: newStatus } });
          toast.success(`Record ${newStatus ? 'disabled' : 'enabled'} successfully`);
        } catch (error) {
          console.error("Disable error:", error);
          toast.error("Failed to update record status");
        } finally {
          setConfirmState({ isOpen: false });
        }
      }
    });
  };

  const loadData = () => {
    refetch();
  };

  // 4. Derived Data (Client-side pagination via hook)
  const filteredContacts = useMemo(() => contacts.filter(c => c.salesType !== 'Direct Sale'), [contacts]);
  const { paginatedData: paginatedLeads, totalPages } = useClientPagination(filteredContacts, page, 10);
  const leads = filteredContacts; // Keep for filter options and stats

  const filterOptions = useMemo(() => {
    return {
      locations: [...new Set(leads.map(l => l.city).filter(Boolean))],
      sources: [...new Set(leads.map(l => l.source || 'Manual'))],
      assignees: telecallers.map(t => ({ id: t._id, name: t.name }))
    };
  }, [leads, telecallers]);

  const {
    activities,
    addActivity,
    isAdding
  } = useContactActivities(detail?._id);

  const actionableUnassignedCount = globalStats.unassigned || 0;

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-2 overflow-x-hidden">
      {/* Brand Hero Banner */}
      <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
        {/* Left Text */}
        <div className="pl-8 z-20">
          <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">Verve Contacts</h1>
          <p className="text-sm font-body text-slate-400 mt-2 font-medium">Verve Kitchens Client Management</p>
        </div>

        {/* Right Kitchen Image with gradient fade */}
        <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
          <img
            src="/kitchens/3.jpg"
            alt="Contact Directory"
            className="absolute top-0 right-0 w-full h-full object-cover object-center"
          />
        </div>
      </div>

      <CustomersStats
        leads={leads}
        isExpanded={isStatsExpanded}
        onToggle={() => setIsStatsExpanded(!isStatsExpanded)}
        user={user}
        onFilterUnassigned={() => {
          setFilters('assignee', 'unassigned');
          setView('assign');
        }}
      />


      <div className="flex flex-col gap-2">
        <CustomersControlPanel
          leads={leads}
          user={user}
          search={search} setSearch={setSearch}
          filters={filters} setFilters={setFilters}
          sortBy={sortBy} setSortBy={setSortBy}
          showAdvancedFilters={showAdvancedFilters} setShowAdvancedFilters={setShowAdvancedFilters}
          filterOptions={filterOptions}
          view={view} setView={setView}
          viewStyle={viewStyle} setViewStyle={setViewStyle}
          onAddClick={() => setAddOpen(true)}
          onBulkClick={(file) => {
            setBulkFile(file);
            setBulkOpen(true);
          }}
          stages={visibleStages}
          onStartAllocation={() => setAssignModalOpen(true)}
          totalUnassigned={actionableUnassignedCount}
          onExport={handleExport}
          isExporting={isExporting}
          activityStats={activityStats}
        />

        {/* Developer : Nithin
            Date : 24-04-2026
            changes: Removed min-h-[400px] to prevent unnecessary down scroll when content is less */}
        <div className="flex-1">
          {view === "uploads" ? (
            viewStyle === "grid" ? (
              <UploadsGridView history={importHistory} onFileClick={() => { }} onRemoveFile={deleteImport} />
            ) : (
              <UploadsHistoryView history={importHistory} onFileClick={() => { }} onRemoveFile={deleteImport} />
            )
          ) : (
            <div className="flex flex-col gap-4">
              {viewStyle === "kanban" ? (
                <CustomersKanban
                  stages={visibleStages}
                  filtered={paginatedLeads}
                  onCustomerClick={setDetail}
                  onStatusChange={updateStatus}
                  onEdit={["field_marketing", "field marketing"].includes((user?.role || "").toLowerCase().trim()) ? undefined : (c) => { setEditData(normalizeCustomer(c)); setEditOpen(true); }}
                  onToggleDisable={["field_marketing", "field marketing"].includes((user?.role || "").toLowerCase().trim()) ? undefined : toggleDisableCustomer}
                  teamMembers={telecallers}
                />
              ) : (
                <CustomersTableView
                  filtered={paginatedLeads}
                  isLoading={isLoading}
                  updateStatus={updateStatus}
                  onCustomerClick={setDetail}
                stages={visibleStages}
                onEdit={(c) => { setEditData(normalizeCustomer(c)); setEditOpen(true); }}
                onToggleDisable={toggleDisableCustomer}
                user={user}
                telecallers={telecallers}
                teamMembers={teamData}
                onUpdateProgressStatus={async (id, progressStatus) => {
                  try {
                    await updateContact({
                      id,
                      data: { progressStatus }
                    });
                    toast.success(`Progress Status updated to ${progressStatus}`);
                  } catch (err) {
                    toast.error("Failed to update progress status");
                  }
                }}
                extraActions={(l) => {
                  const currentProg = l.progressStatus || "Initially Not Started";
                  const isDesignerRole = (user?.role || "").toLowerCase().trim() === "designer" || (user?.role || "").toLowerCase().trim().includes("designer");
                  if (
                    (l.status === 'initial_design' || isDesignerRole) &&
                    (isAdmin || isDesignerRole) &&
                    currentProg !== "Completed"
                  ) {
                    return (
                      <TableActionButton
                        icon={UploadCloud}
                        onClick={() => setDesignUploadLead(l)}
                        title="Upload initial design"
                      />
                    );
                  }
                  return null;
                }}
                onAssignStaff={async (id, staffId) => {
                  await updateContact({
                    id,
                    data: {
                      assignedTo: staffId,
                      telecaller: staffId,
                      status: staffId ? "assigned" : "new"
                    }
                  });
                }}
                onAssignDesigner={async (id, designerId) => {
                  await updateContact({
                    id,
                    data: {
                      designer: designerId
                    }
                  });
                }}
              />
            )}
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>

    <ContactModals
        detail={activeContact} setDetail={setDetail}
        activities={activities}
        actNote={actNote} setActNote={setActNote}
        onAddActivity={async (note) => {
          await addActivity({ note: note || actNote });
          setActNote("");
        }}
        updateStatus={updateStatus}
        addOpen={addOpen} setAddOpen={setAddOpen}
        editOpen={editOpen} setEditOpen={setEditOpen}
        editData={editData} setEditData={setEditData}
        bulkOpen={bulkOpen} setBulkOpen={setBulkOpen}
        bulkFile={bulkFile} setBulkFile={setBulkFile}
        bulkImport={bulkImport}
        filteredLeads={leads}
        assignModalOpen={assignModalOpen} setAssignModalOpen={setAssignModalOpen}
        loadData={loadData}
        pendingStatus={pendingStatus} setPendingStatus={setPendingStatus}
        confirmStatusUpdate={confirmStatusUpdate}
        stages={visibleStages}
        user={user}
      />

      {designUploadLead && (
        <DesignerUpload
          isOpen={!!designUploadLead}
          onClose={() => setDesignUploadLead(null)}
          lead={designUploadLead}
        />
      )}

      {revertRequest && (
        <RequestRevertModal
          isOpen={true}
          onClose={() => setRevertRequest(null)}
          contact={revertRequest.contact}
          targetStatus={revertRequest.targetStatus}
          onRequested={() => setRevertRequest(null)}
          isOwner={isAdmin || (user?.role || "").toLowerCase().trim() === "owner"}
          onDirectRevert={handleDirectRevert}
        />
      )}

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState({ isOpen: false })}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        loading={confirmState.loading}
      />
    </div>
  );
}


